import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import {
  getPassportCampaignDetail,
  getPassportFlags,
  isPassportEnabled,
  joinPassportCampaign,
  type PassportCampaignObject,
  type PassportCampaignSummary,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE, formatEncounterDate } from './passportCopy';
import { PassportChrome } from './PassportChrome';

const OBJECT_KIND_LABELS: Record<string, string> = {
  checkpoint: 'Checkpoint',
  booth: 'Booth',
  activation: 'Activation',
  destination: 'Destination',
};

function objectKindLabel(kind?: string): string {
  if (!kind) return 'Waypoint';
  return OBJECT_KIND_LABELS[kind] || kind.replace(/_/g, ' ');
}

function campaignTypeLabel(type: string): string {
  return type.replace(/_/g, ' ');
}

/**
 * Passport hunt progress destination.
 * Signature: large N-of-M progress plate + waypoint track (history, not a game scoreboard).
 */
const PassportCampaignDetailPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('found') || '';
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [campaign, setCampaign] = useState<PassportCampaignSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', {
        state: { from: `/passport/campaigns/${encodeURIComponent(slug)}` },
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, navigate, slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPassportCampaignDetail(slug);
      setCampaign(data);
      void apiService.trackEvent('passport_campaign_viewed', {
        metadata: { view: 'detail', slug, campaign_id: data.id },
      });
    } catch (err: unknown) {
      setCampaign(null);
      setError(err instanceof Error ? err.message : 'Campaign not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags)) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  const handleJoin = async () => {
    if (!campaign) return;
    setJoining(true);
    try {
      await joinPassportCampaign({ campaign_id: campaign.id });
      showToast(`Joined ${campaign.title}.`, 'success');
      void apiService.trackEvent('passport_campaign_joined', {
        metadata: { campaign_id: campaign.id, source: 'detail' },
      });
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to join', 'error');
    } finally {
      setJoining(false);
    }
  };

  const objects = campaign?.objects || [];
  const found = campaign?.participation?.objects_visited_count ?? objects.filter((o) => o.visited).length;
  const total = (() => {
    const fromPart = campaign?.participation?.required_objects_total;
    if (typeof fromPart === 'number' && fromPart > 0) return fromPart;
    const required = objects.filter((o) => o.is_required !== false).length;
    return required > 0 ? required : objects.length;
  })();
  const complete =
    campaign?.participation?.status === 'completed' ||
    (total > 0 && found >= total && Boolean(campaign?.participation));
  const joined = Boolean(campaign?.participation);

  const progressDots = useMemo(() => {
    const n = Math.max(total, 0);
    if (n <= 0) return [];
    return Array.from({ length: Math.min(n, 12) }, (_, i) => i < found);
  }, [found, total]);

  if (authLoading || !isAuthenticated || flagsLoading) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) return null;

  if (!isPassportEnabled(flags)) {
    return (
      <PassportChrome active="campaigns" flags={flags} back={{ label: 'Hunts', to: '/passport/campaigns' }}>
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PassportChrome>
    );
  }

  if (loading) {
    return (
      <PassportChrome
        active="campaigns"
        flags={flags}
        back={{ label: 'Hunts', to: '/passport/campaigns' }}
        title={
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">Hunt</h1>
        }
      >
        <p className="text-gray-400 text-sm" role="status">
          Loading hunt…
        </p>
      </PassportChrome>
    );
  }

  if (error || !campaign) {
    return (
      <PassportChrome
        active="campaigns"
        flags={flags}
        back={{ label: 'Hunts', to: '/passport/campaigns' }}
        title={
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">Hunt</h1>
        }
      >
        <EmptyState
          title="Hunt not found"
          description={error || 'This Passport hunt may have ended or the link is outdated.'}
          action={
            <Button type="button" variant="primary" onClick={() => navigate('/passport/campaigns')}>
              Browse hunts
            </Button>
          }
        />
      </PassportChrome>
    );
  }

  return (
    <PassportChrome
      active="campaigns"
      flags={flags}
      back={{ label: 'Hunts', to: '/passport/campaigns' }}
      title={
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            {campaign.title}
          </h1>
          {campaign.description ? (
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">{campaign.description}</p>
          ) : null}
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="uppercase tracking-wide text-orange-300/80">{campaignTypeLabel(campaign.campaign_type)}</span>
        {campaign.sponsor_name ? <span>· {campaign.sponsor_name}</span> : null}
        {campaign.starts_at || campaign.ends_at ? (
          <span>
            ·{' '}
            {campaign.starts_at ? `Starts ${formatEncounterDate(campaign.starts_at)}` : ''}
            {campaign.ends_at ? `${campaign.starts_at ? ' · ' : ''}Ends ${formatEncounterDate(campaign.ends_at)}` : ''}
          </span>
        ) : null}
      </div>

      {/* Signature progress plate */}
      <section
        aria-label="Hunt progress"
        className="relative overflow-hidden rounded-2xl border border-orange-500/25 bg-[#0c0c0c] mb-8"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(249,115,22,0.16),transparent_55%)]"
        />
        <div className="relative px-5 py-6 sm:px-7 sm:py-8">
          <p className="text-[11px] uppercase tracking-[0.14em] text-orange-300/90 mb-3">
            {complete ? 'Collection complete' : joined ? 'In progress' : 'Not joined yet'}
          </p>
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1 mb-5">
            <span className="font-display text-5xl sm:text-6xl text-white tabular-nums leading-none">{found}</span>
            <span className="text-gray-500 text-xl sm:text-2xl pb-1">of</span>
            <span className="font-display text-5xl sm:text-6xl text-white/80 tabular-nums leading-none">{total}</span>
            <span className="text-sm text-gray-400 pb-1.5 ml-1">waypoints found in person</span>
          </div>

          {progressDots.length > 0 ? (
            <div
              className="flex flex-wrap gap-2 mb-2"
              role="img"
              aria-label={`${found} of ${total} waypoints found`}
            >
              {progressDots.map((lit, i) => (
                <span
                  key={i}
                  className={`h-2.5 w-2.5 rounded-full border ${
                    lit
                      ? 'bg-orange-500 border-orange-400 shadow-[0_0_12px_rgba(249,115,22,0.45)]'
                      : 'bg-transparent border-white/20'
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {!joined ? (
        <div className="mb-8 flex flex-wrap gap-3">
          <Button type="button" variant="primary" loading={joining} onClick={() => void handleJoin()}>
            Join hunt
          </Button>
          <p className="text-sm text-gray-400 self-center max-w-md">
            Joining records your progress when you scan waypoints at the show.
          </p>
        </div>
      ) : (
        <div className="mb-8 flex flex-wrap gap-3">
          <Link to="/scan" className="btn-primary inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg">
            Keep scanning
          </Link>
          <Link to="/passport" className="btn-secondary inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg">
            Your Passport
          </Link>
        </div>
      )}

      <section aria-labelledby="hunt-waypoints-heading">
        <h2 id="hunt-waypoints-heading" className="heading-section mb-3">
          Waypoints
        </h2>
        <p className="text-sm text-gray-500 mb-4 leading-relaxed max-w-prose">
          Found stops show by name. Hidden ones stay unnamed until you meet them in person.
        </p>

        {objects.length === 0 ? (
          <EmptyState title="No waypoints yet" description="The organizer has not published checkpoints for this hunt." />
        ) : (
          <ol className="divide-y divide-white/[0.06] rounded-xl border border-white/[0.08] overflow-hidden bg-[#0c0c0c]/50">
            {objects.map((obj: PassportCampaignObject, index: number) => {
              const isHidden = Boolean(obj.hidden);
              const isVisited = Boolean(obj.visited);
              const isHighlight = highlightId !== '' && (obj.public_id === highlightId || String(obj.id) === highlightId);
              const label = isHidden && !isVisited ? 'Still out there' : obj.title || 'Waypoint';
              const kind = isHidden && !isVisited ? 'Hidden' : objectKindLabel(obj.object_kind);

              return (
                <li
                  key={obj.public_id || String(obj.id) || index}
                  className={`px-4 py-4 sm:px-5 transition-colors ${
                    isHighlight ? 'bg-orange-500/10' : isVisited ? 'bg-white/[0.02]' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${
                        isVisited
                          ? 'border-orange-400/60 bg-orange-500/15 text-orange-300'
                          : 'border-white/[0.12] bg-[#141414] text-gray-500'
                      }`}
                    >
                      {isVisited ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                          <path
                            d="M2.5 6.2L4.8 8.5L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        obj.sequence_order ?? index + 1
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[11px] uppercase tracking-wide text-orange-300/80">{kind}</p>
                        {isVisited ? (
                          <span className="text-[11px] uppercase tracking-wide text-emerald-300/90">Found</span>
                        ) : null}
                        {isHighlight ? (
                          <span className="text-[11px] uppercase tracking-wide text-orange-200">Just now</span>
                        ) : null}
                      </div>
                      <p className={`mt-0.5 font-medium ${isHidden && !isVisited ? 'text-gray-500 italic' : 'text-white'}`}>
                        {label}
                      </p>
                      {!isHidden && obj.location_label ? (
                        <p className="text-sm text-gray-500 mt-0.5">{obj.location_label}</p>
                      ) : null}
                      {!isHidden && obj.description ? (
                        <p className="text-sm text-gray-400 mt-1 leading-relaxed">{obj.description}</p>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </PassportChrome>
  );
};

export default PassportCampaignDetailPage;
